import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlusCircle, Star } from 'lucide-react';
import { PlayerSearchResultItemProps } from './types';
import { formatDateString as formatDate } from '../../lib/utils';

// Helper function to get status color classes
const getStatusColorClasses = (status: string): string => {
  switch (status) {
    case 'considering':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'offered':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'rostered':
    case 'accepted':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'declined':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

interface PlayerSearchResultItemPropsWithRatings extends PlayerSearchResultItemProps {
  avgRating?: number | null;
  loadingRating?: boolean;
}

const PlayerSearchResultItem = ({ player, onAddPlayer, teamStatus, avgRating, loadingRating, onViewDetails }: PlayerSearchResultItemPropsWithRatings) => {
  // avgRating and loadingRating are now props from parent

  return (
    <tr className="border-b last:border-b-0 hover:bg-gray-50">
      {/* Player Name and Info Column */}
      <td className="py-3 px-4">
        <div className="font-medium">
          {player.first_name} {player.last_name}
        </div>
        <div className="flex flex-wrap gap-1 mt-1 items-center">
          {/* Show player positions and competitiveness if available */}
          {player.profile?.positions?.map(pos => (
            <Badge key={pos} variant="default" className="text-xs font-normal">
              {pos}
            </Badge>
          ))}
          {player.profile?.competitiveness?.map(comp => (
            <Badge key={comp} variant="outline" className="text-xs font-normal">
              {comp}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap gap-1 mt-1 items-center">
          {/* Team status pills */}
          {teamStatus && teamStatus.length > 0 && teamStatus.map((status: any) => (
            <span 
              key={status.teamId} 
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${getStatusColorClasses(status.status)}`}
            >
              {status.teamName} - {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
            </span>
          ))}
        </div>
        {/*
          Only show actions on small screens (mobile/tablet)
          Use Tailwind's responsive classes: block on sm and below, hidden on md and up
        */}
        <div className="flex flex-wrap gap-1 mt-1 items-center block sm:hidden">
          {onViewDetails && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(player.id)}
              className="text-blue-600 hover:text-blue-800"
            >
              View Details
            </Button>
          )}

          {!teamStatus || teamStatus.length === 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddPlayer(player)}
            >
              <PlusCircle className="mr-1 h-4 w-4" /> Consider
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddPlayer(player)}
              disabled={teamStatus.some((status: any) => status.currentTeam)}
            >
              <PlusCircle className="mr-1 h-4 w-4" /> Consider
            </Button>
          )}
        </div>
      </td>

      {/* Birthdate and Age Group Column */}
      <td className="py-3 px-4">
        <div className="text-sm text-gray-600">
          {player.birthdate ? formatDate(player.birthdate) : 'No birthdate'}
        </div>
        {player.age_group && (
          <div className="text-sm text-gray-600">
            {player.age_group.sex === 'male' ? 'Boys' : 'Girls'} {player.age_group.group_name}
          </div>
        )}
      </td>

      {/* Ratings Column */}
      <td className="py-3 px-4">
        {loadingRating ? (
          <span className="text-xs text-gray-400">Loading...</span>
        ) : typeof avgRating === 'number' ? (
          <span className="flex items-center text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5 font-semibold w-fit">
            <Star className="w-4 h-4 mr-1 text-yellow-500" fill="#facc15" />
            {avgRating.toFixed(2)}
          </span>
        ) : (
          <span className="text-xs text-gray-400">No rating</span>
        )}
      </td>

      {/* Actions Column */}
      <td className="py-3 px-4 hidden sm:table-cell">
        <div className="flex items-center space-x-2">
          {onViewDetails && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(player.id)}
              className="text-blue-600 hover:text-blue-800"
            >
              View Details
            </Button>
          )}

          {!teamStatus || teamStatus.length === 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddPlayer(player)}
            >
              <PlusCircle className="mr-1 h-4 w-4" /> Consider
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddPlayer(player)}
              disabled={teamStatus.some((status: any) => status.currentTeam)}
            >
              <PlusCircle className="mr-1 h-4 w-4" /> Consider
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default PlayerSearchResultItem;